import React, { useEffect, useState, useCallback } from "react";
import LoginForm from "./LoginForm";
import SignupForm from "./SignupForm";
import ForgotPasswordForm from "./ForgotPasswordForm";
import ResetPasswordForm from "./ResetPasswordForm";
import { Sun, Moon } from "lucide-react";
import StorageUtil from "../../utils/serviceUtils/storageUtil";
import { isElectron } from "react-device-detect";

interface AuthContainerProps {
  initialView?: "login" | "signup" | "forgot" | "reset";
}

const AuthContainer: React.FC<AuthContainerProps> = ({ initialView = "login" }) => {
  const [view, setView] = useState<"login" | "signup" | "forgot" | "reset">(initialView);

  console.log("view", view);

  // Theme state management with system preference support
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const appSkin = StorageUtil.getReaderConfig("appSkin");

    // If no preference is set, default to system
    if (!appSkin) {
      StorageUtil.setReaderConfig("appSkin", "system");
    }

    // Determine if dark mode should be active
    if (appSkin === "night" || appSkin === "dark") {
      document.documentElement.classList.add("dark");
      return true;
    } else if (appSkin === "system") {
      // Check system preference
      let systemIsDark = false;
      if (isElectron) {
        try {
          const { ipcRenderer } = window.require("electron");
          systemIsDark = ipcRenderer.sendSync("system-color");
          StorageUtil.setReaderConfig("isOSNight", systemIsDark ? "yes" : "no");
        } catch (e) {
          // Fallback to media query if electron API fails
          systemIsDark = window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches;
          StorageUtil.setReaderConfig("isOSNight", systemIsDark ? "yes" : "no");
        }
      } else {
        systemIsDark = window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches;
        StorageUtil.setReaderConfig("isOSNight", systemIsDark ? "yes" : "no");
      }

      if (systemIsDark) {
        document.documentElement.classList.add("dark");
        return true;
      } else {
        document.documentElement.classList.remove("dark");
        return false;
      }
    } else {
      document.documentElement.classList.remove("dark");
      return false;
    }
  });

  useEffect(() => {
    setView(initialView);
  }, [initialView]);

  // Listen to system preference changes
  useEffect(() => {
    const appSkin = StorageUtil.getReaderConfig("appSkin");
    if (appSkin === "system") {
      const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
      const handleChange = (e: MediaQueryListEvent) => {
        // Check again in case user changed preference while listening
        const currentAppSkin = StorageUtil.getReaderConfig("appSkin");
        if (currentAppSkin === "system") {
          if (e.matches) {
            document.documentElement.classList.add("dark");
            setIsDarkMode(true);
          } else {
            document.documentElement.classList.remove("dark");
            setIsDarkMode(false);
          }
        }
      };

      // For Electron, we might need to listen differently, but for web this works
      if (!isElectron && mediaQuery.addEventListener) {
        mediaQuery.addEventListener("change", handleChange);
        return () => mediaQuery.removeEventListener("change", handleChange);
      } else if (!isElectron && mediaQuery.addListener) {
        // Fallback for older browsers
        mediaQuery.addListener(handleChange);
        return () => mediaQuery.removeListener(handleChange);
      }
    }
  }, []);

  const switchToSignup = () => setView("signup");
  const switchToLogin = () => setView("login");
  const switchToForgot = () => setView("forgot");
  const switchToReset = () => setView("reset");

  const handleThemeToggle = useCallback(() => {
    const appSkin = StorageUtil.getReaderConfig("appSkin");
    let newAppSkin: string;
    let newDarkMode: boolean;

    if (appSkin === "system") {
      // If currently on system, switch to explicit light
      newAppSkin = "light";
      newDarkMode = false;
    } else if (appSkin === "night" || appSkin === "dark") {
      // If dark, switch to light
      newAppSkin = "light";
      newDarkMode = false;
    } else {
      // If light, switch to dark
      newAppSkin = "night";
      newDarkMode = true;
    }

    StorageUtil.setReaderConfig("appSkin", newAppSkin);

    if (newDarkMode) {
      document.documentElement.classList.add("dark");
    } else {
      document.documentElement.classList.remove("dark");
    }

    setIsDarkMode(newDarkMode);
  }, []);

  return (
    <div className="min-h-screen w-full h-full p-6 bg-gray-50 dark:bg-gray-900 flex items-center justify-center transition-colors duration-200">
      {/* Theme Toggle Button */}
      <button
        onClick={handleThemeToggle}
        className="fixed top-6 right-6 z-50 p-3 rounded-full bg-white dark:bg-gray-800 shadow-lg hover:shadow-xl transition-all duration-200 border border-gray-200 dark:border-gray-700 hover:scale-110 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-gray-900"
        aria-label={isDarkMode ? "Switch to light mode" : "Switch to dark mode"}
        title={isDarkMode ? "Switch to light mode" : "Switch to dark mode"}
      >
        {isDarkMode ? <Moon className="w-5 h-5 text-gray-300" /> : <Sun className="w-5 h-5 text-yellow-500" />}
      </button>

      <div className="w-full h-full grid grid-cols-1 lg:grid-cols-2 gap-8 items-stretch">
        <div className="w-full mx-auto overflow-y-auto pr-1 flex items-center justify-center max-w-xl">
          {view === "login" && <LoginForm onSwitchToSignup={switchToSignup} onSwitchToForgot={switchToForgot} />}
          {view === "signup" && <SignupForm onSwitchToLogin={switchToLogin} />}
          {view === "forgot" && <ForgotPasswordForm onSwitchToLogin={switchToLogin} />}
          {view === "reset" && <ResetPasswordForm onSwitchToLogin={switchToLogin} />}
        </div>
        <div className="hidden lg:block w-full">
          <div className="relative w-full h-full min-h-[640px] rounded-2xl overflow-hidden shadow-lg bg-gray-900 dark:bg-gray-800 transition-colors duration-200">
            <img
              src="/assets/SKTP.png"
              alt="Welcome visual"
              className="absolute inset-0 w-full h-full object-cover opacity-80 dark:opacity-70 transition-opacity duration-200"
            />
            <div className="absolute inset-0 bg-gray-900/40 dark:bg-gray-900/60 transition-colors duration-200" />
            <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8 text-gray-100 dark:text-gray-200">
              <p className="text-lg md:text-xl font-medium italic">
                "A reader lives a thousand lives before he dies. The man who never reads lives only one."
              </p>
              <p className="mt-2 text-sm text-gray-300 dark:text-gray-400">— George R. R. Martin</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthContainer;
